/**
 * Generation truth (canonical spec §I, "Generation"): the going-to-air transition shows only
 * stages that correspond to a real request state. No percentages, no timers pretending to be work.
 */
import type { DirectorError, DirectorLanguage } from '@clutch/core';
import { pick } from './i18n';

export const GENERATION_STAGES = ['research', 'compose', 'facts', 'voice', 'sync'] as const;
export type GenerationStage = (typeof GENERATION_STAGES)[number];
export type StageStatus = 'done' | 'active' | 'waiting';

/**
 * research = the frozen game evidence (snapshot + plays) is fetched in this browser;
 * compose  = the Director request is in flight (the model researches and writes server-side);
 * facts    = the response is bound to the frozen cutoff and its fact audit is read;
 * voice    = synthesis with the selected voice is in flight;
 * sync     = the first chapter's audio is buffered before chapter one starts.
 */
export function stageStatuses(current: GenerationStage): Record<GenerationStage, StageStatus> {
  const at = GENERATION_STAGES.indexOf(current);
  return Object.fromEntries(GENERATION_STAGES.map((s, i) => [s, i < at ? 'done' : i === at ? 'active' : 'waiting'])) as Record<GenerationStage, StageStatus>;
}

export const STAGE_COPY: Record<GenerationStage, { en: string; hi: string; zh: string }> = {
  research: { en: 'Researching the game', hi: 'मैच की पड़ताल', zh: '研究比赛' },
  compose: { en: 'Composing your show', hi: 'आपका शो लिखा जा रहा है', zh: '编排你的节目' },
  facts: { en: 'Checking every fact', hi: 'हर तथ्य की जाँच', zh: '核对每个事实' },
  voice: { en: 'Producing the voice', hi: 'आवाज़ तैयार हो रही है', zh: '制作配音' },
  sync: { en: 'Syncing the scenes', hi: 'दृश्य मिलाए जा रहे हैं', zh: '同步画面' },
};

/** Human copy for a typed Director failure. The title never claims a show exists. */
export function unavailableCopy(e: Pick<DirectorError, 'code'>, l: DirectorLanguage): { title: string; body: string } {
  switch (e.code) {
    case 'AUDIO_UNAVAILABLE':
      return { title: pick({ en: 'Voice unavailable', hi: 'आवाज़ उपलब्ध नहीं', zh: '语音不可用' }, l), body: pick({ en: 'Narration could not be produced or played. Retry, or choose another voice. Playback has stopped.', hi: 'नैरेशन बन या चल नहीं सका। फिर कोशिश करें या दूसरी आवाज़ चुनें। प्लेबैक रोक दिया गया है।', zh: '无法生成或播放解说语音。请重试或选择其他声音，播放已停止。' }, l) };
    case 'UNSUPPORTED_LOCALE':
      return { title: pick({ en: 'Language not available yet', hi: 'यह भाषा अभी उपलब्ध नहीं', zh: '该语言暂不可用' }, l), body: pick({ en: 'This language is not available end-to-end on this server. Pick another language to create your broadcast.', hi: 'दूसरी भाषा चुनें।', zh: '请选择其他语言。' }, l) };
    case 'DIRECTOR_VALIDATION_FAILED':
      return { title: pick({ en: 'AI Director unavailable', hi: 'AI डायरेक्टर उपलब्ध नहीं', zh: 'AI导演不可用' }, l), body: pick({ en: 'The show the model wrote did not pass the fact check, so it was not aired.', hi: 'मॉडल का लिखा शो तथ्य-जाँच में पास नहीं हुआ, इसलिए दिखाया नहीं गया।', zh: '模型撰写的节目未通过事实核查，因此未播出。' }, l) };
    default:
      return { title: pick({ en: 'AI Director unavailable', hi: 'AI डायरेक्टर उपलब्ध नहीं', zh: 'AI导演不可用' }, l), body: pick({ en: 'No show was created. Clutch never swaps in a template story.', hi: 'कोई शो नहीं बना। Clutch कभी टेम्पलेट कहानी नहीं दिखाता।', zh: '未生成节目。Clutch不会用模板故事替代。' }, l) };
  }
}
